import { useCallback, useMemo, useState } from 'react';
import { Post } from '../model/post.types';
import { useDeletePost } from '../api/useDeletePost';
import { usePublishPost } from '../api/usePublishPost';
import { usePinPostAction } from '../lib/usePinPostAction';
import { analytics } from '../lib/analytics';
import { useCommunityContext } from '../lib/communityContext';
import { ActionMenu, ActionMenuItem } from './ActionMenu';
import { ActionConfirmModal } from './ActionConfirmModal';
import { SchedulePostModal } from './SchedulePostModal';

type Props = {
  post: Post;
  pinnedCount: number;
  onEdit?: () => void;
};

export const OwnPostActionsMenu = ({ post, pinnedCount, onEdit }: Props) => {
  const { communityId } = useCommunityContext();
  const deletePost = useDeletePost();
  const publishPost = usePublishPost();
  const pin = usePinPostAction(post, communityId, pinnedCount);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [confirmingPostNow, setConfirmingPostNow] = useState(false);
  const [scheduling, setScheduling] = useState(false);

  const openDelete = useCallback(() => setConfirmingDelete(true), []);
  const dismiss = useCallback(() => setConfirmingDelete(false), []);
  const confirmDelete = useCallback(() => {
    analytics.trackPostDeleted(post);
    deletePost({ communityId, postId: post.id });
    setConfirmingDelete(false);
  }, [deletePost, communityId, post]);

  const postNow = useCallback(() => {
    setConfirmingPostNow(false);
    void publishPost({ communityId, postId: post.id }).then(({ published }) => {
      if (published) analytics.trackPostCreatedFromDraft(post);
    });
  }, [publishPost, communityId, post]);

  const isScheduled = post.status === 'scheduled';
  const isUnpublished = isScheduled || post.status === 'draft';

  const items = useMemo<ActionMenuItem[]>(() => {
    const entries: Array<ActionMenuItem | null> = [
      isUnpublished
        ? null
        : {
            key: 'pin',
            label: 'Pin to top',
            disabled: pin.disabled,
            onClick: pin.toggle,
            toggle: { checked: pin.checked, helperText: pin.helperText },
          },
      onEdit ? { key: 'edit', label: 'Edit post', onClick: onEdit } : null,
      isUnpublished
        ? {
            key: 'schedule',
            label: isScheduled ? 'Reschedule' : 'Schedule',
            onClick: () => setScheduling(true),
          }
        : null,
      isUnpublished
        ? {
            key: 'postNow',
            label: 'Post now',
            onClick: () => setConfirmingPostNow(true),
          }
        : null,
      {
        key: 'delete',
        label: 'Delete post',
        destructive: true,
        onClick: openDelete,
      },
    ];
    return entries.filter((item): item is ActionMenuItem => item !== null);
  }, [
    isUnpublished,
    isScheduled,
    pin.disabled,
    pin.toggle,
    pin.checked,
    pin.helperText,
    onEdit,
    openDelete,
  ]);

  return (
    <>
      <ActionMenu items={items} />
      <ActionConfirmModal
        open={confirmingDelete}
        title="Are you sure you want to delete this post?"
        actionLabel="Delete"
        onConfirm={confirmDelete}
        onDismiss={dismiss}
      />
      <ActionConfirmModal
        open={confirmingPostNow}
        title="Post now?"
        body="Are you sure you want to publish this post to Community now?"
        actionLabel="Post now"
        tone="primary"
        onConfirm={postNow}
        onDismiss={() => setConfirmingPostNow(false)}
      />
      {scheduling && (
        <SchedulePostModal
          open
          initialWhen={post.publishedAt}
          actionLabel={isScheduled ? 'Reschedule' : 'Schedule'}
          onConfirm={(when) => {
            setScheduling(false);
            void publishPost({ communityId, postId: post.id, publishAt: when });
          }}
          onDismiss={() => setScheduling(false)}
        />
      )}
    </>
  );
};
